import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiEndpoint, readUseOpenAIFlag } from "../utils/env";
import { createDatabase } from "../api/adminApi";

/**
 * Manages all state and logic for the DB-creation wizard embedded in ResultsPanel.
 *
 * @param {{ token: string, dbs: Array, t: Function }} params
 * @returns wizard state and handlers
 */
export function useDbCreationWizard({ token, dbs, t }) {
  const navigate = useNavigate();

  const [wizardStep, setWizardStep] = useState(null);
  const [wizardDbName, setWizardDbName] = useState("");
  const [wizardDbError, setWizardDbError] = useState("");
  const [wizardDbDescription, setWizardDbDescription] = useState("");
  const [wizardInput, setWizardInput] = useState("");
  const [wizardMessages, setWizardMessages] = useState([]);

  const [assistantQuestion, setAssistantQuestion] = useState("");
  const [assistantAnswer, setAssistantAnswer] = useState("");
  const [assistantLoading, setAssistantLoading] = useState(false);
  const [assistantError, setAssistantError] = useState("");
  const [creatingDb, setCreatingDb] = useState(false);

  function resetWizard() {
    setWizardStep(null);
    setWizardDbError("");
    setWizardInput("");
    setAssistantAnswer("");
    setAssistantError("");
    setAssistantQuestion("");
    setWizardMessages([]);
  }

  async function askAssistant(question, context) {
    const q = (question || "").trim();
    const useOpenAIFlag = readUseOpenAIFlag();
    if (!q || !token || !useOpenAIFlag) return;

    setWizardMessages((prev) => [
      ...prev,
      { id: Date.now().toString() + "-user", role: "user", text: q },
    ]);
    setAssistantLoading(true);
    setAssistantError("");
    try {
      const resp = await fetch(apiEndpoint("/api/AiAssist/chat"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ message: q, context }),
      });

      const txt = await resp.text();
      let json;
      try {
        json = txt ? JSON.parse(txt) : null;
      } catch {
        json = { answer: txt };
      }

      if (!resp.ok) {
        const rawMsg =
          (json && json.message) ||
          (typeof json === "string" ? json : resp.statusText);

        if (resp.status === 404) {
          throw new Error(
            t("results.wizardAssistantUnavailable", {
              defaultValue:
                "The assistant is not enabled on this server right now.",
            })
          );
        }
        if (resp.status === 500) {
          if (
            typeof rawMsg === "string" &&
            rawMsg.toLowerCase().includes("apikey is not configured")
          ) {
            throw new Error(
              t("results.wizardAssistantMisconfigured", {
                defaultValue:
                  "The assistant is not configured for this environment.",
              })
            );
          }
          throw new Error(
            t("results.wizardAssistantError", {
              defaultValue:
                "The assistant is currently unavailable. Please try again later.",
            })
          );
        }

        throw new Error(
          rawMsg ||
            t("results.wizardAssistantError", {
              defaultValue: "The assistant request failed.",
            })
        );
      }

      setAssistantAnswer(json?.answer || "");
      if (json?.answer) {
        setWizardMessages((prev) => [
          ...prev,
          {
            id: Date.now().toString() + "-ai",
            role: "ai",
            text: json.answer,
          },
        ]);
      }
    } catch (err) {
      setAssistantError(err?.message || "Failed to ask assistant.");
    } finally {
      setAssistantLoading(false);
    }
  }

  function looksLikeQuestion(text) {
    const s = (text || "").trim();
    if (!s) return false;
    const lower = s.toLowerCase();
    if (s.endsWith("?")) return true;
    return /^(can|how|what|why|when|where|should|could|do|does|is|are|may|will|would)\b/.test(
      lower
    );
  }

  function extractDbNameFromInput(text) {
    const raw = (text || "").trim();
    if (!raw) return "";

    const quoted = raw.match(/["']([^"']+)["']/);
    if (quoted && quoted[1]) return quoted[1].trim();

    const m1 = raw.match(
      /\b(?:name|call)\s+(?:it|this|the database|the db|db)\s+(.+)$/i
    );
    if (m1 && m1[1]) return m1[1].trim().replace(/[.!?]+$/, "");

    const m2 = raw.match(/\b(?:name|call)\s+(.+)$/i);
    if (m2 && m2[1]) return m2[1].trim().replace(/[.!?]+$/, "");

    const parts = raw.split(/[:,;-]/);
    const last = parts[parts.length - 1].trim();
    if (last && last.length <= 80) return last.replace(/[.!?]+$/, "");

    return raw;
  }

  async function handleWizardSubmit(e) {
    e.preventDefault();
    const text = (wizardInput || "").trim();
    if (!text || !wizardStep) return;

    const context = JSON.stringify({
      wizardStep,
      wizardDbName: (wizardDbName || "").trim() || null,
      wizardDbDescription: (wizardDbDescription || "").trim() || null,
    });
    const useOpenAIFlag = readUseOpenAIFlag();
    const isQuestion = looksLikeQuestion(text);
    setWizardDbError("");
    setAssistantError("");

    if (!(useOpenAIFlag && isQuestion)) {
      setWizardMessages((prev) => [
        ...prev,
        { id: Date.now().toString() + "-user", role: "user", text },
      ]);
    }

    if (wizardStep === "create-db-name") {
      if (useOpenAIFlag && isQuestion) {
        setAssistantQuestion(text);
        await askAssistant(text, context);
      } else {
        const trimmed = extractDbNameFromInput(text);
        if (!trimmed) {
          setWizardDbError(
            t("results.wizardDbNameRequired", {
              defaultValue: "Please enter a name.",
            })
          );
          setWizardInput("");
          return;
        }
        const exists = (dbs || []).some((d) => {
          const n = d?.dbName || d?.name || d?.displayName;
          return (
            typeof n === "string" &&
            n.trim().toLowerCase() === trimmed.toLowerCase()
          );
        });
        if (exists) {
          setWizardDbError(
            t("results.wizardDbNameExists", {
              defaultValue:
                "There is already a database with this name. Try a different one.",
            })
          );
          setWizardInput("");
          return;
        }
        setWizardDbName(trimmed);
        try {
          localStorage.setItem(
            "wizard_db_name_draft",
            JSON.stringify({ name: trimmed, ts: Date.now() })
          );
        } catch {}
        setWizardStep("create-db-confirm");
        setWizardMessages((prev) => [
          ...prev,
          {
            id: Date.now().toString() + "-ai-confirm",
            role: "ai",
            text: t("results.wizardDbConfirm", {
              name: trimmed,
              defaultValue:
                "We'll prepare a new database called '{{name}}'. Does that look right?",
            }),
          },
        ]);
      }
    } else if (wizardStep === "create-db-confirm") {
      const lower = text.toLowerCase();
      const yes =
        /^(yes|y|ok|okay)$/.test(lower) || lower.includes("looks good");
      const change =
        /^(no|n)$/.test(lower) ||
        lower.includes("change name") ||
        lower.includes("rename");

      if (yes) {
        setWizardStep("create-db-desc");
        setWizardMessages((prev) => [
          ...prev,
          {
            id: Date.now().toString() + "-user-yes",
            role: "user",
            text,
          },
          {
            id: Date.now().toString() + "-ai-desc",
            role: "ai",
            text: t("results.wizardAskDbDesc", {
              defaultValue: "Anything you'd like to note about it? (optional)",
            }),
          },
        ]);
      } else if (change) {
        setWizardStep("create-db-name");
        setWizardMessages((prev) => [
          ...prev,
          {
            id: Date.now().toString() + "-user-change",
            role: "user",
            text,
          },
        ]);
      } else if (useOpenAIFlag && isQuestion) {
        setAssistantQuestion(text);
        await askAssistant(text, context);
      } else {
        setWizardDbError(
          t("results.wizardConfirmHint", {
            defaultValue: "Type 'yes' to accept or 'no' to change the name.",
          })
        );
      }
    } else if (wizardStep === "create-db-desc") {
      const lower = text.toLowerCase();
      const skip =
        lower.includes("skip") ||
        lower.includes("no description") ||
        lower === "none";

      if (skip) {
        try {
          localStorage.setItem(
            "wizard_db_meta_draft",
            JSON.stringify({
              name: (wizardDbName || "").trim(),
              description: null,
              ts: Date.now(),
            })
          );
        } catch {}
        setWizardDbDescription("");
        setWizardMessages((prev) => [
          ...prev,
          {
            id: Date.now().toString() + "-user-skip",
            role: "user",
            text,
          },
        ]);
      } else if (useOpenAIFlag && isQuestion) {
        setAssistantQuestion(text);
        await askAssistant(text, context);
      } else {
        const desc = text;
        setWizardDbDescription(desc);
        try {
          localStorage.setItem(
            "wizard_db_meta_draft",
            JSON.stringify({
              name: (wizardDbName || "").trim(),
              description: desc,
              ts: Date.now(),
            })
          );
        } catch {}
      }
    }

    setWizardInput("");
  }

  async function handleCreateFromWizard() {
    const name = (wizardDbName || "").trim();
    if (!name) {
      setWizardDbError(
        t("results.wizardDbNameRequired", {
          defaultValue:
            "We couldn't detect a database name yet. Please enter one above.",
        })
      );
      return;
    }
    if (!token) {
      setWizardDbError(
        t("results.wizardAuthRequired", {
          defaultValue: "You need to be signed in to create a database.",
        })
      );
      return;
    }

    setCreatingDb(true);
    setWizardDbError("");
    try {
      try {
        localStorage.setItem(
          "wizard_db_meta_draft",
          JSON.stringify({
            name,
            description: (wizardDbDescription || "").trim() || null,
            ts: Date.now(),
          })
        );
      } catch {}

      await createDatabase(token, name);
      navigate("/admin/indexes");
    } catch (e) {
      setWizardDbError(
        (e && e.message) ||
          t("results.wizardCreateDbError", {
            defaultValue:
              "Creating the database failed. You can also try again from Admin → Indexes.",
          })
      );
    } finally {
      setCreatingDb(false);
    }
  }

  return {
    wizardStep,
    setWizardStep,
    wizardDbName,
    wizardDbError,
    setWizardDbError,
    wizardDbDescription,
    setWizardDbDescription,
    wizardInput,
    setWizardInput,
    wizardMessages,
    setWizardMessages,
    assistantQuestion,
    assistantAnswer,
    assistantLoading,
    assistantError,
    creatingDb,
    resetWizard,
    askAssistant,
    looksLikeQuestion,
    extractDbNameFromInput,
    handleWizardSubmit,
    handleCreateFromWizard,
  };
}
